import Image from 'next/image';
import { downloadCompraPDF } from '@/services/api/compras.service';
import { getCompraAccessToken } from '@/helpers/compraAccessToken';
import { useEvento } from '@/store/evento/evento';
import { useEventoContext } from '../../contexts';

type Props = {
  status: 'pendiente' | 'en proceso' | 'confirmada' | 'rechazada' | null;
  message?: string;
  tickets?: Array<{
    id: string;
    codigo: string;
    tipoEntradaId: string;
    status: string;
    mesa?: string | null;
    asiento?: string | null;
  }>;
  pdfUrl?: string | null;
  compraId?: string | null;
}

export function CompraCompletada({ status, message, tickets, pdfUrl, compraId }: Props) {
  const isSuccess = status === 'confirmada';
  const isRejected = !isSuccess && status !== 'pendiente';

  const { state: funcion } = useEvento.use.funcion();
  const { evento } = useEventoContext();

  const fechaFormat = funcion?.fecha ? new Date(funcion.fecha).toLocaleDateString('es-AR') : '';

  /**
   * Descarga el PDF usando el ID de la compra
   */
  const handleDownloadPDF = async () => {
    if (!compraId) return;

    try {
      const blob = await downloadCompraPDF(compraId, '');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `entradas-${compraId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al descargar PDF:', error);
    }
  };


  return (
    <div className="flex flex-col items-center justify-center min-h-[200px] space-y-6 text-white">
      {/* Ícono de estado */}
      <div className={`w-20 h-20 rounded-full flex items-center justify-center ${isSuccess
        ? 'bg-green-500/20 border-4 border-green-400'
        : isRejected
          ? 'bg-red-500/20 border-4 border-red-400'
          : 'bg-gray-500/20 border-4 border-gray-400'
        }`}>
        {isSuccess ? (
          <svg className="w-12 h-12 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : isRejected ? (
          <svg className="w-12 h-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )}
      </div>

      {/* Título */}
      <div className="text-center space-y-2">
        <h2 className={`text-2xl font-display ${isSuccess ? 'text-green-400' : isRejected ? 'text-red-400' : 'text-gray-400'
          }`}>
          {isSuccess ? '¡Compra Confirmada!' : isRejected ? 'Compra Rechazada' : 'Estado Desconocido'}
        </h2>
        <p className="text-white/80 text-sm">
          {message || (isSuccess ? 'Tu compra se ha procesado correctamente' : 'No se pudo procesar tu compra')}
        </p>
      </div>

      {/* Lista de tickets (solo si está confirmada) */}
      {isSuccess && tickets && tickets.length > 0 && (
        <div className="w-full space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Tus Entradas</h3>
            {compraId && (
              <button
                onClick={handleDownloadPDF}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Descargar Entradas (PDF)
              </button>
            )}
          </div>

          <div className="space-y-2 max-h-96 overflow-y-auto">
            {tickets.map((ticket) => (
              <div
                key={ticket.id}
                className="bg-white/10 border border-white/20 rounded-lg p-3 flex items-center gap-3"
              >
                {/* Información del ticket */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{evento?.nombre} : {fechaFormat}</p>
                  {ticket.mesa && ticket.asiento ? (
                    <p className="text-xs text-white/60 mt-0.5">Mesa {ticket.mesa} Asiento {ticket.asiento}</p>
                  ) : (
                    <p className="text-xs text-white/60 mt-0.5">Código: {ticket.codigo}</p>
                  )}
                </div>

                {/* Estado */}
                <div className={`px-2 py-1 rounded text-xs flex-shrink-0 ${ticket.status === 'activo'
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-gray-500/20 text-gray-400'
                  }`}>
                  {ticket.status}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mensaje adicional para rechazada */}
      {isRejected && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
          <p className="text-sm text-white/80">
            Por favor, intenta nuevamente o contacta con soporte si el problema persiste.
          </p>
        </div>
      )}
    </div>
  );
}

